<template>
<div>
    <m-edit-text
        label="Organization"
        :value="rate_card.organization_label"
        read_only
        v-if="rate_card != null && !compact"
    />

    <v-switch
      label="Is Revenue Account"
      v-on:change="onChangeRevenueAccount"
      v-model="rate_card.organization_account.is_revenue_account"
    />

    <m-edit-select
        label="MRR State"
        v-model="rate_card.selected_rate_card.mrr_state"
        :possible_values="$rc.enumValues('mrr_state')"
        v-on:input="onChangeMrrState"
        :return-object="false"
    />

    <m-edit-select-rate-card
        :value="rate_card.selected_rate_card.rate_card_id"
        v-on:input="onChangeRateCard"
        label="Rate Card (Starting)"
        v-if="rate_card != null"
    />

    <m-edit-textarea
        :value="rate_card.selected_rate_card.custom_rate_card_label"
        label="Rate Card Custom Description / Notes"
        v-on:input="onSetCustomRateCardLabel"
        optional
    />

    <m-edit-text
        label="Minimum User Count"
        :value="rate_card.selected_rate_card.mrr_minimum_user_count"
        v-on:input="onChangeMinimumUsers"
        v-if="rate_card != null"
        integer
        style="margin-bottom: 7px"
    />

    <v-data-table
     v-if="rate_card != null"
        :headers="headers"
        :items="rate_card.effective_rate_card"
        :items-per-page="100"
        class="elevation-1"
        :hide-default-footer="true"

        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
            }"
    >

        <template v-slot:item.setup="{ item }">
            <div
                v-bind:class="{ 'custom' : !item.effective_fee_setup_is_default, 'editable' : isEditableSetupFee(item) }"
                v-on:click="onClickCustomizeSetupFee(item)"
            >
                {{prettyPrintSetupFee(item)}}
            </div>
        </template>

        <template v-slot:item.count="{ item }">
            <div
                v-bind:class="{ 'custom' : !item.effective_count_is_default, 'editable' : isEditableCount(item) }"
                v-on:click="onClickCustomizeIncluded(item)"
            >
                {{prettyPrintCount(item)}}
            </div>
        </template>

        <template v-slot:item.fee="{ item }" >
            <div
                v-bind:class="{ 'custom' : !item.effective_fee_is_default, 'editable' : isEditableFee(item) }"
                v-on:click="onClickCustomizeFee(item)"
            >
                {{prettyPrintPrice(item)}}
            </div>
        </template>

        <template v-slot:item.fee_milli_percent="{ item }" >
            <div
                v-bind:class="{ 'custom' : !item.effective_fee_milli_percent_is_default, 'editable' : isEditableFeeMilliPercent(item) }"
                v-on:click="onClickCustomizeFeeMilliPercent(item)"
            >
                {{prettyPrintMillipercent(item)}}
            </div>
        </template>
        

     </v-data-table>

     <m-customize-rate-card-value-dialog
        ref="customize_dialog"
    />

     <p
        style="margin-top: 15px;"
     >
         Values in <span class="custom">bold</span> have been customized from the default rate card.  Click a cell to edit its value.
     </p>
     <p>
         Please note: a value will be marked as "custom" if it has been set to anything other than "match default".  This applies even if the custom value is the same as the <i>current</i> default value.
    </p>

</div>
</template>

<script>

const Millicents = require('@/js/millicents.js');

export default {
    props: {

        organization_id: {
            type: String,
            default: function() { return null; }
        },

        compact: {
            type: Boolean,
            default: function() { return false; }
        },
    },

    data() {
        return {
            rate_card: null,

            headers: [
                { 'text' : 'Item', 'value' : 'line_type' },
                { 'text' : 'Setup Fee', 'value' : 'setup' },
                { 'text' : '# Included', 'value' : 'count' },
                { 'text' : 'Fee', 'value' : 'fee' },
                { 'text' : 'Percentage', 'value' : 'fee_milli_percent' },
            ],
        }
    },

    mounted () {
        this.update();
    },

    methods: {
        update() {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'GET: /api/v1/billing/organization/rate-card/:organization_id',
            
                'params' : {
                        'organization_id': this.organization_id,
                    },
            
                'on_success': (data) => {
                        this.rate_card_label = data.selected_rate_card.rate_card_label;
                        this.rate_card = data;
                    },
            
                'on_error': 'Unable to load rate card',
            });
        },

        onChangeRateCard(new_rate_card_id)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/billing/organization',
            
                'params' : {
                        'organization_id' : this.organization_id,
                        'rate_card_id' : new_rate_card_id,
                    },
            
                'on_success': (data) => {
                        this.update();
                    },
            
                'on_error': 'Unable to save change to rate card',
            });
        },

        onChangeRevenueAccount(value)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/billing/organization',
            
                'params' : {
                        'organization_id' : this.organization_id,
                        'is_revenue_account' : value,
                    },
            
                'on_success': (data) => {
                        this.update();
                    },
            
                'on_error': 'Unable to save change to revenue account status',
            });
        },

        onSetCustomRateCardLabel(value)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/billing/organization',
            
                'params' : {
                        'organization_id': this.rate_card.organization_id,
                        'custom_rate_card_label': value,
                    },
            
                'on_success': (data) => {
                        this.update();
                    },
            
                'on_error': 'Unable to save change to minimum user count',
            });
        },

        onChangeMinimumUsers(new_count)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/billing/organization',
            
                'params' : {
                        'organization_id' : this.organization_id,
                        'mrr_minimum_user_count' : new_count,
                    },
            
                'on_success': (data) => {
                        this.update();
                    },
            
                'on_error': 'Unable to save change to minimum user count',
            });
        },

        onChangeMrrState(new_value)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/organizations/mrr',
            
                'params' : {
                    'organization_id' : this.organization_id,
                        'mrr_state' : new_value,
                },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to update organization MRR state',
            });
        },

        prettyPrintSetupFee(line)
        {
            if ( line.effective_fee_setup_milli_cents == 0 && !this.isEditableSetupFee(line) )
                return '';

            return Millicents.prettyPrint(line.effective_fee_setup_milli_cents);
        },

        prettyPrintMillipercent(line)
        {
            if ( line.effective_fee_milli_percent == 0 && !this.isEditableFeeMilliPercent(line) )
                return '';

            return Millicents.prettyPrintMillipercent(line.effective_fee_milli_percent);
        },

        prettyPrintPrice(line)
        {
            if ( line.effective_fee_milli_cents == 0 && !this.isEditableFee(line) )
                return '';

            return Millicents.prettyPrint(line.effective_fee_milli_cents);
        },

        prettyPrintCount(line)
        {
            if ( line.effective_count_included == 0 && !this.isEditableCount(line) )
                return '';

            return line.effective_count_included.toLocaleString();
        },

        isEditableSetupFee(line)
        {
            let type = line.line_type;

            return type == 'subscription' || type == 'tcr-brand' || type == 'tcr-campaign';
        },

        isEditableFee(line)
        {
            return true;
        },

        isEditableCount(line)
        {
            let type = line.line_type;

            return !(type == 'subscription' || type == 'credit' || type == 'tcr-brand' || type == 'tcr-campaign');
        },

        isEditableFeeMilliPercent(line)
        {
            let type = line.line_type;

            return type == 'collection';
        },

        onClickCustomizeSetupFee(line)
        {
            let params = {
                'line_type' : line.line_type,
                'value_type' : 'setup_fee',
                'default_value' : line.default_fee_setup_milli_cents,
                'effective_value' : line.effective_fee_setup_milli_cents,
                'rate_card_label' : line.rate_card_label,
                'on_edit' : (val) => { this.onEditSetupFee(line,val); },
                'on_default' : (val) => {}
            };

            this.$refs['customize_dialog'].showDialog(params);
        },

        onClickCustomizeFeeMilliPercent(line)
        {
            let params = {
                'line_type' : line.line_type,
                'value_type' : 'milli_percent',
                'default_value' : line.default_fee_milli_percent,
                'effective_value' : line.effective_fee_milli_percent,
                'rate_card_label' : line.rate_card_label,
                'on_edit' : (val) => { this.onEditFeeMilliPercent(line,val); },
                'on_default' : (val) => {}
            };

            this.$refs['customize_dialog'].showDialog(params);
        },

        onClickCustomizeIncluded(line)
        {
            let params = {
                'line_type' : line.line_type,
                'value_type' : 'included_count',
                'default_value' : line.default_count_included,
                'effective_value' : line.effective_count_included,
                'rate_card_label' : line.rate_card_label,
                'on_edit' : (val) => { this.onEditIncluded(line,val); },
                'on_default' : (val) => {}
            };

            this.$refs['customize_dialog'].showDialog(params);
        },

        onClickCustomizeFee(line)
        {
            let params = {
                'line_type' : line.line_type,
                'value_type' : 'fee',
                'default_value' : line.default_fee_milli_cents,
                'effective_value' : line.effective_fee_milli_cents,
                'rate_card_label' : line.rate_card_label,
                'on_edit' : (val) => { this.onEditFee(line,val); },
                'on_default' : (val) => {}
            };

            this.$refs['customize_dialog'].showDialog(params);
        },

        onEditSetupFee(line, new_setup_fee)
        {
            let custom_line = this.createCustomLine(line);
            custom_line.fee_setup_milli_cents = new_setup_fee;
            this.putCustomLine(custom_line);
        },

        onEditFeeMilliPercent(line, new_value)
        {
            let custom_line = this.createCustomLine(line);
            custom_line.fee_milli_percent = new_value;
            this.putCustomLine(custom_line);
        },

        onEditIncluded(line, new_included)
        {
            let custom_line = this.createCustomLine(line);
            custom_line.count_included = new_included;
            this.putCustomLine(custom_line);
        },

        onEditFee(line, new_fee)
        {
            let custom_line = this.createCustomLine(line);
            custom_line.fee_milli_cents = new_fee;
            this.putCustomLine(custom_line);
        },

      

        putCustomLine(custom_line)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT-LINE: /api/v1/billing/organization/rate-card',
            
                'params' : custom_line,
            
                'on_success': (data) => {
                        this.update();
                    },
            
                'on_error': 'Unable to customize line',
            });
        },

        createCustomLine(line_src)
        {
            return {
                'organization_id' : line_src.organization_id,
                'line_type' : line_src.line_type,
                'count_included' : line_src.effective_count_is_default ? null : line_src.effective_count_included,
                'fee_milli_cents' : line_src.effective_fee_is_default ? null : line_src.effective_fee_milli_cents,
                'fee_setup_milli_cents' : line_src.effective_fee_setup_is_default ? null : line_src.effective_fee_setup_milli_cents,
                'fee_milli_percent': line_src.effective_fee_milli_percent ? null : line_src.effective_fee_milli_percent,
            };
        }
    },
}
</script>

<style lang="scss" scoped>
.custom
{
    font-weight: 700;
    color: blue;
}

.editable
{
    cursor: pointer;
    width: 100%;
    height: 100%;

    padding-top: 14px;
}
</style>